import { NgClass } from "@angular/common";
import { Component, computed, inject, signal, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AccountMarketplace, MatchType, StrategyType, Targeting } from "@front/m19-api-client";
import { StrategyGroupEx, StrategyTargetingType } from "@front/m19-models";
import { Constant, SpStrategyService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  IBadgeComponent,
  IButtonDirective,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupPrefixDirective,
  IToggleComponent,
  ITooltipDirective,
  MODAL_DATA,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AsinItemComponent } from "@m19-board/shared/asin-item/asin-item.component";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { combineLatest, map, Observable, of, switchMap } from "rxjs";
import {
  StrategyKeywordModalComponent,
  StrategyKeywordModalData,
  StrategyKeywordModalOutput,
} from "./strategy-keyword-modal.component";
import { SpStrategyEx } from "@front/m19-models/strategy/SpStrategyEx";

export interface TargetingsModalData {
  strategy: SpStrategyEx;
  strategyType: StrategyType;
  targetingType: StrategyTargetingType;
  strategyGroup: StrategyGroupEx;
  accountMarketplace: AccountMarketplace;
  targetings: Targeting[];
  isReadOnly?: boolean;
}

@Component({
  selector: "app-targetings-modal",
  imports: [
    IButtonDirective,
    ITooltipDirective,
    IBadgeComponent,
    AsinItemComponent,
    NgClass,
    TranslocoDirective,
    AsinSelectionComponent,
    IToggleComponent,
    IInputGroupDirective,
    IInputGroupInput,
    FormsModule,
    IInputGroupPrefixDirective,
    IIconComponent,
  ],
  templateUrl: "./targetings-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TargetingsModalComponent {
  private translocoService = inject(TranslocoService);

  readonly modalRef = inject(ModalRef);
  readonly modalService = inject(ModalService);
  readonly toastrService = inject(ToasterService);
  readonly spStrategyService = inject(SpStrategyService);

  readonly MAX_STRATEGY_ASINS = Constant.maxAsinTargetingByStrategy;

  readonly modalData = inject<TargetingsModalData>(MODAL_DATA);

  @ViewChild("productTargetingsModal", { static: false }) productTargetingsModal!: TemplateRef<any>;
  advertisedProductsModalRef?: ModalRef<any, any>;

  searchKeywordQuery = signal<string>("");
  searchProductQuery = signal<string>("");

  targetingType = signal<StrategyTargetingType>(this.modalData.targetingType);

  asinIsolation = signal(this.modalData.strategy.asinIsolation);
  isUpdatingAsinIsolation = signal(false);

  keywords = signal<Targeting[]>(this.modalData.targetings.filter((t) => t.matchType !== MatchType.asinSameAs));
  products = signal<Targeting[]>(this.modalData.targetings.filter((t) => t.matchType === MatchType.asinSameAs));
  productTargetings = computed(() => this.products().map((t) => t.targetingValue));
  allTargetings = computed(() => [...this.keywords(), ...this.products()]);

  newProductTargetings = signal<string[]>([]);

  shownKeywords = computed(() => {
    const regex = new RegExp(this.searchKeywordQuery(), "i");
    return this.keywords().filter((t) => regex.test(t.targetingValue));
  });
  shownProducts = computed(() => {
    const regex = new RegExp(this.searchProductQuery(), "i");
    return this.products().filter((t) => regex.test(t.targetingValue));
  });

  toggleTargetingType() {
    if (this.allTargetings().length > 0) {
      const modalRef = this.modalService.openModal(ConfirmPopupComponent, {
        modalTitle: `Change to ${this.targetingType() == StrategyTargetingType.KEYWORDS ? "product" : "keyword"} targeting`,
        data: {
          message: `This will remove the current ${this.allTargetings().length} ${
            this.targetingType() == StrategyTargetingType.KEYWORDS ? "keyword" : "product"
          } targeting${this.allTargetings().length > 1 ? "s" : ""} from the strategy`,
        },
      });
      modalRef
        .afterClosed()
        .pipe(
          switchMap(() =>
            this.spStrategyService.removeTargetingFromStrategy(
              this.modalData.strategy,
              this.allTargetings(),
              this.modalData.accountMarketplace.resourceOrganizationId!,
            ),
          ),
        )
        .subscribe({
          next: () => {
            this.toastrService.success(
              this.translocoService.translate("targetings-modal.all_targetings_removed_from_strategy"),
              this.translocoService.translate("targetings-modal.strategy_targetings_updated"),
            );
            this.keywords.set([]);
            this.products.set([]);
            this.setTargetingType(
              this.targetingType() === StrategyTargetingType.KEYWORDS
                ? StrategyTargetingType.PRODUCTS
                : StrategyTargetingType.KEYWORDS,
            );
          },
          error: (error: string) => {
            this.toastrService.error(
              this.translocoService.translate("targetings-modal.error_removing_strategy_targetings_error", [error]),
              this.translocoService.translate("targetings-modal.strategy_targetings_update_error"),
            );
          },
        });
    } else {
      this.setTargetingType(
        this.targetingType() === StrategyTargetingType.KEYWORDS
          ? StrategyTargetingType.PRODUCTS
          : StrategyTargetingType.KEYWORDS,
      );
    }
  }

  private setTargetingType(type: StrategyTargetingType) {
    this.targetingType.set(type);
  }

  updateIsolation(isolation: boolean) {
    this.isUpdatingAsinIsolation.set(true);
    this.spStrategyService
      .updateStrategy({
        accountId: this.modalData.strategy.accountId,
        marketplace: this.modalData.strategy.marketplace,
        strategyId: this.modalData.strategy.strategyId!,
        organizationId: this.modalData.accountMarketplace.resourceOrganizationId!,
        asinIsolation: isolation,
        asinsToAdd: [],
        asinsToDelete: [],
      })
      .subscribe({
        next: (updatedStrategy) => {
          const successToasterMessage = isolation
            ? "targetings-modal.enable_asin_level_isolation_for_strategy"
            : "targetings-modal.disabled_asin_level_isolation_for_strategy";

          this.toastrService.success(
            this.translocoService.translate(successToasterMessage, [updatedStrategy.name ?? ""]),
            this.translocoService.translate("targetings-modal.asin_level_isolation_update"),
          );
          this.asinIsolation.set(updatedStrategy.asinIsolation);
          this.isUpdatingAsinIsolation.set(false);
        },
        error: (e: string) => {
          this.isUpdatingAsinIsolation.set(false);
          this.toastrService.error(
            e,
            this.translocoService.translate("targetings-modal.strategy_isolation_update_error"),
          );
        },
      });
  }

  editKeywordTargetings() {
    const ref = this.modalService.openModal<StrategyKeywordModalData, StrategyKeywordModalOutput>(
      StrategyKeywordModalComponent,
      {
        size: "lg",
        height: "fill",
        modalTitle: this.translocoService.translate("targetings-modal.edit_keyword_targetings"),
        data: {
          marketplace: this.modalData.accountMarketplace.marketplace,
          keywords: this.keywords(),
          isReadOnly: !!this.modalData.isReadOnly,
        },
      },
    );

    ref
      .afterClosed()
      .pipe(
        switchMap((result) => {
          return combineLatest([this.addKeywords(result.keywordsToAdd), this.deleteKeywords(result.keywordsToDelete)]);
        }),
      )
      .subscribe({
        next: ([addedKeywords, deletedKeywords]) => {
          if (addedKeywords) {
            this.keywords.set([...this.keywords(), ...addedKeywords]);
          }
          if (deletedKeywords) {
            this.keywords.set([
              ...this.keywords().filter(
                (k) =>
                  !deletedKeywords.find((d) => d.targetingValue === k.targetingValue && d.matchType === k.matchType),
              ),
            ]);
          }
          this.toastrService.success(this.translocoService.translate("targetings-modal.strategy_targeting_updated"));
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("targetings-modal.error_adding_strategy_keyword", [error]),
            this.translocoService.translate("targetings-modal.strategy_targeting_update_error"),
          );
        },
      });
  }

  private addKeywords(targetings: Targeting[]): Observable<Targeting[] | undefined> {
    const filteredTargetings = targetings.filter(
      (t) => !this.keywords().find((a) => a.targetingValue == t.targetingValue),
    );

    if (filteredTargetings.length == 0) {
      return of(undefined);
    }
    return this.spStrategyService
      .addTargetingToStrategy(
        this.modalData.strategy,
        filteredTargetings,
        this.modalData.accountMarketplace.resourceOrganizationId!,
      )
      .pipe(map((_) => filteredTargetings));
  }

  private deleteKeywords(targetings: Targeting[]): Observable<Targeting[] | undefined> {
    if (targetings.length == 0) {
      return of(undefined);
    }
    return this.spStrategyService
      .removeTargetingFromStrategy(
        this.modalData.strategy,
        targetings,
        this.modalData.accountMarketplace.resourceOrganizationId!,
      )
      .pipe(map((_) => targetings));
  }

  editProductTargetings() {
    this.advertisedProductsModalRef = this.modalService.openModal(this.productTargetingsModal!, {
      size: "xl",
      height: "fill",
    });
  }

  updateProductTargetings(targetings: string[]) {
    if (targetings.length == 0) {
      return;
    } else {
      const toAdd = targetings.filter(
        (a) =>
          !this.products()
            .map((t) => t.targetingValue)
            .includes(a),
      );
      const toRemove = this.products()
        .map((t) => t.targetingValue)
        .filter((a) => !targetings.includes(a));

      if (toAdd.length > 0) {
        this.addProduct(toAdd);
      }
      if (toRemove.length > 0) {
        this.deleteProduct(toRemove);
      }
      this.newProductTargetings.set([]);
      this.advertisedProductsModalRef?.close();
    }
  }

  private addProduct(asins: string[]) {
    if (asins.length == 0) {
      return;
    }
    const filtered: Targeting[] = asins.map((a) => ({ targetingValue: a, matchType: MatchType.asinSameAs }));

    this.spStrategyService
      .addTargetingToStrategy(
        this.modalData.strategy,
        filtered,
        this.modalData.accountMarketplace.resourceOrganizationId!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("targetings-modal.product_targeting_s_added_to_strategy"),
            this.translocoService.translate("targetings-modal.strategy_targeting_updated"),
          );
          this.products.set([...this.products(), ...filtered]);
          this.modalData.strategy.targetings = [...this.keywords(), ...this.products()];
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("targetings-modal.error_adding_strategy_product_targeting_s_error", [
              error,
            ]),
            this.translocoService.translate("targetings-modal.strategy_targeting_update_error"),
          );
        },
      });
  }

  private deleteProduct(asins: string[]) {
    const targetings: Targeting[] = asins.map((a) => ({ targetingValue: a, matchType: MatchType.asinSameAs }));

    this.spStrategyService
      .removeTargetingFromStrategy(
        this.modalData.strategy,
        targetings,
        this.modalData.accountMarketplace.resourceOrganizationId!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("targetings-modal.product_targeting_s_removed_from_strategy"),
            this.translocoService.translate("targetings-modal.strategy_targeting_updated"),
          );
          this.products.update((products) =>
            products.filter((a) => !targetings.map((t) => t.targetingValue).includes(a.targetingValue)),
          );
          this.modalData.strategy.targetings = [...this.keywords(), ...this.products()];
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("targetings-modal.error_removing_strategy_product_targeting_s_error", [
              error,
            ]),
            this.translocoService.translate("targetings-modal.strategy_targeting_update_error"),
          );
        },
      });
  }

  readonly StrategyTargetingType = StrategyTargetingType;
  readonly MatchType = MatchType;
  readonly StrategyType = StrategyType;
}
